/**
 * Confounder generation for the engagement gate: orthographic near-misses of
 * a word, used as distractors. The one copy — retells and lekterion both
 * import it, and apps/reader will. It replaces
 * `apps/lekterion/src/lib/model/confounders.ts` (ported from lekterion @
 * `d942f2f`, the `pre_ante_twix` char-colocation heuristic from sara's
 * `worksheets/app/data/word_options`) and `apps/retells/src/reader/
 * confounders.ts` (a port of that one), keeping what each had that the other
 * had lost:
 *
 * - lekterion's `Scorer` / `composeScorers` seam and the `alphabet` the
 *   ingest writes into every tables file (byte-for-byte what it wrote
 *   before);
 * - retells' `applySurfacePattern`, which restores the typographic quote a
 *   fold flattened as well as the case, so the one curly-apostrophe chip is
 *   not the answer.
 *
 * A confounder is a single code-point substitution of the word, same length,
 * scored by three colocation signals learned from a corpus:
 *   - `ante[a][b]`  count of bigram "ab"  → how often `b` follows `a`
 *   - `pre[b][a]`   the same bigram keyed by its second char
 *   - `twix[a+c][b]` count of trigram "abc" → how often `b` sits betwixt `a`,`c`
 * For a substitution `x` at a position with neighbours L, R the default score
 * is `ante[L][x] + pre[R][x] + 2·twix[LR][x]`. Alphabet-agnostic throughout:
 * the tables read whatever code points the text contains, so polytonic Greek,
 * German umlauts/ß and Latin work without any `[a-z]` assumption.
 *
 * NORMALIZER-FREE BY DESIGN. Every function here takes words already in dict
 * form, or takes the app's normalizer as an argument. The two apps key their
 * dicts differently, deliberately: retells with `foldQuote` (NFC + casefold +
 * quote fold — it must match the `aiCache.quoteLower` consensus key), lekterion
 * with NFC + casefold (it keys live `fluencyRecords` / `confounderHits` rows and
 * the ingest's per-language tables, so changing it is a rekey, not a refactor).
 * Neither is `contracts/word.extractNormalizedWord`, which keys sara's fluency
 * records and is frozen. Pure module apart from the explicit WeakMap memo at
 * the bottom.
 */

/** Char-colocation frequency tables — the "pre-ante-twix" dict. */
export interface ColocationTables {
  /** `pre[c]` = predecessors of `c` → weighted count. */
  pre: Record<string, Record<string, number>>;
  /** `ante[c]` = successors of `c` → weighted count. */
  ante: Record<string, Record<string, number>>;
  /** `twix[c1c2]` = chars seen between the pair `c1`,`c2` → weighted count. */
  twix: Record<string, Record<string, number>>;
  /** Distinct code points observed, sorted — reference / future feature space. */
  alphabet: string[];
}

/** A surface → dict-form normalizer. Each app supplies its own (see header). */
export type NormalizeWord = (surface: string) => string;

/** Code points of a string (surrogate-safe, unlike UTF-16 indexing). */
function cps(s: string): string[] {
  return Array.from(s);
}

function bump(
  table: Record<string, Record<string, number>>,
  key: string,
  sub: string,
  by: number,
): void {
  const row = (table[key] ??= {});
  row[sub] = (row[sub] ?? 0) + by;
}

/**
 * Build the colocation tables from a word→frequency map (normalized words
 * with occurrence counts, so common spellings dominate the statistics). Pure
 * and deterministic — the same input yields byte-identical JSON, which the
 * lekterion ingest depends on to avoid churning its deploy.
 */
export function buildColocation(freq: Map<string, number>): ColocationTables {
  const pre: ColocationTables["pre"] = {};
  const ante: ColocationTables["ante"] = {};
  const twix: ColocationTables["twix"] = {};
  const alphabet = new Set<string>();

  for (const [word, count] of freq) {
    const w = cps(word);
    // Each loop's bound is what makes every subscript in it in-range, so the
    // `| undefined` noUncheckedIndexedAccess adds is unreachable here.
    const at = (k: number): string => w[k]!;
    for (const ch of w) alphabet.add(ch);
    for (let i = 0; i + 1 < w.length; i++) {
      const a = at(i);
      const b = at(i + 1);
      bump(ante, a, b, count); // b follows a
      bump(pre, b, a, count); // a precedes b
    }
    for (let i = 0; i + 2 < w.length; i++) {
      bump(twix, at(i) + at(i + 2), at(i + 1), count); // middle sits betwixt
    }
  }

  return { pre, ante, twix, alphabet: [...alphabet].sort() };
}

/**
 * The word-frequency map of a document: every token's surface, normalized,
 * counted. Tokenizers in this workspace store only `[start, end)` offsets, so
 * surfaces are sliced from the immutable text here.
 */
export function wordFrequencies(
  model: { text: string; tokens: readonly { start: number; end: number }[] },
  normalize: NormalizeWord,
): Map<string, number> {
  const freq = new Map<string, number>();
  for (const t of model.tokens) {
    const word = normalize(model.text.slice(t.start, t.end));
    if (word === "") continue;
    freq.set(word, (freq.get(word) ?? 0) + 1);
  }
  return freq;
}

/** One candidate substitution, handed to a {@link Scorer}. */
export interface Candidate {
  /** The full confounder string (same length as the source word). */
  form: string;
  /** Substitution position (code-point index). */
  pos: number;
  /** The replacement code point. */
  char: string;
  /** Neighbouring code points, or null at a word edge. */
  left: string | null;
  right: string | null;
}

/** Scores a candidate substitution; higher = more plausible. 0 ⇒ discard. */
export type Scorer = (
  c: Candidate,
  word: string,
  tables: ColocationTables,
) => number;

/** Default scorer: the pre-ante-twix colocation sum. */
export const colocationScorer: Scorer = (c, _word, t) => {
  let score = 0;
  if (c.left !== null) score += t.ante[c.left]?.[c.char] ?? 0;
  if (c.right !== null) score += t.pre[c.right]?.[c.char] ?? 0;
  if (c.left !== null && c.right !== null)
    score += 2 * (t.twix[c.left + c.right]?.[c.char] ?? 0);
  return score;
};

/**
 * Compose scorers additively — the seam for layering grapheme/phoneme
 * features, or a weight from lekterion's `confounderHits` tally, onto the
 * colocation sum without touching a consumer.
 */
export function composeScorers(...scorers: Scorer[]): Scorer {
  return (c, word, t) => scorers.reduce((sum, s) => sum + s(c, word, t), 0);
}

export interface GetOptionsConfig {
  /** Max confounders to return (ranked by descending plausibility). */
  limit?: number;
  scorer?: Scorer;
}

/**
 * Ranked confounders for one (already normalized) word. Each is a single
 * code-point substitution, same length, scored by the tables; the word itself
 * is never returned. Deterministic: score desc, then code-point order as a
 * stable tiebreak. Empty/1-char words and words with no attested
 * substitution return `[]` — the gate falls open for them.
 */
export function getOptions(
  word: string,
  tables: ColocationTables,
  { limit = 12, scorer = colocationScorer }: GetOptionsConfig = {},
): string[] {
  const w = cps(word);
  const scored = new Map<string, number>();

  for (let p = 0; p < w.length; p++) {
    const left = p > 0 ? (w[p - 1] ?? null) : null;
    const right = p + 1 < w.length ? (w[p + 1] ?? null) : null;
    // Candidate replacements: the union of what the three tables have seen in
    // this neighbourhood — bounds us to corpus-attested letters.
    const candidates = new Set<string>();
    if (left !== null)
      for (const k of Object.keys(tables.ante[left] ?? {})) candidates.add(k);
    if (right !== null)
      for (const k of Object.keys(tables.pre[right] ?? {})) candidates.add(k);
    if (left !== null && right !== null)
      for (const k of Object.keys(tables.twix[left + right] ?? {}))
        candidates.add(k);

    for (const char of candidates) {
      if (char === w[p]) continue;
      const form = w.slice(0, p).join("") + char + w.slice(p + 1).join("");
      // Unreachable while `char !== w[p]` holds (a one-code-point change
      // cannot rebuild the word), kept from retells' port as the invariant
      // stated where it matters: the word is never its own distractor.
      if (form === word) continue;
      const score = scorer({ form, pos: p, char, left, right }, word, tables);
      if (score <= 0) continue;
      const prev = scored.get(form);
      if (prev === undefined || score > prev) scored.set(form, score);
    }
  }

  return [...scored.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, limit)
    .map(([form]) => form);
}

/** ASCII quotes and the typographic form a quote fold flattened them from. */
const CURLY_FOR_ASCII: Record<string, string> = { "'": "’", '"': "”" };

/**
 * Re-apply the source word's per-position surface onto a normalized
 * confounder so the distractor reads like the selected word — else the odd
 * chip gives the answer away. Two things are restored, for the same reason:
 *
 * - CASE, so the one capitalized chip is not the answer;
 * - the QUOTE GLYPH, so the one chip with a typographic apostrophe is not
 *   either. Reader text is educated (`curlQuotes`) while a quote-folding
 *   normalizer folds to ASCII, so `hält’s` would otherwise stand beside
 *   `halt's`. Under a normalizer that does not fold quotes the confounder
 *   already carries the curly glyph and this step is a no-op.
 *
 * No-op when the code-point lengths differ (rare casefold edge, e.g. ß→SS).
 */
export function applySurfacePattern(template: string, lower: string): string {
  const t = cps(template);
  const l = cps(lower);
  if (t.length !== l.length) return lower;
  return l
    .map((ch, i) => {
      // Only where the confounder ALREADY has a quote — never turn one of its
      // own letters into a mark it does not have.
      const curly = CURLY_FOR_ASCII[ch];
      if (curly !== undefined && t[i] === curly) return curly;
      return t[i] !== t[i]?.toLowerCase() ? ch.toUpperCase() : ch;
    })
    .join("");
}

/**
 * Per-document memo of runtime colocation tables (built once per chapter
 * model, shared by every gate opened in it) — for an app that derives its
 * tables from the text in memory rather than shipping them per language.
 * Keyed on the model object and the normalizer: chapter models are immutable
 * for the life of a reading view, and the entry falls away with either.
 */
const tablesByNormalizer = new WeakMap<
  NormalizeWord,
  WeakMap<object, ColocationTables>
>();

export function colocationForModel(
  model: { text: string; tokens: readonly { start: number; end: number }[] },
  normalize: NormalizeWord,
): ColocationTables {
  let byModel = tablesByNormalizer.get(normalize);
  if (byModel === undefined) {
    byModel = new WeakMap();
    tablesByNormalizer.set(normalize, byModel);
  }
  const cached = byModel.get(model);
  if (cached) return cached;
  const tables = buildColocation(wordFrequencies(model, normalize));
  byModel.set(model, tables);
  return tables;
}
